import { useRef, type KeyboardEvent, type ReactNode } from 'react';

export interface Tab {
  id: string;
  label: string;
  icon?: ReactNode;
}

export interface TabsProps {
  tabs: Tab[];
  active: string;
  onChange: (id: string) => void;
  trailing?: ReactNode;
  /**
   * v6-polish — extra className applied to the outer flex wrapper. The
   * App.tsx header uses this to push the tabs visually below the
   * title row (via `order-2`) while keeping them first in DOM order so
   * the keyboard tab sequence starts with the tabs.
   */
  className?: string;
}

/**
 * Tab strip with WAI-ARIA APG keyboard navigation:
 *   - ArrowLeft / ArrowRight cycle focus between tabs (with wrapping).
 *   - Home / End jump to first / last tab.
 *   - Tab key exits the tablist (roving tabindex).
 *
 * Each tab button has `id="tab-{id}"` so the corresponding
 * `aria-labelledby="tab-{id}"` on the `<tabpanel>` resolves correctly
 * (fixes the BLOCKER finding from the v4 a11y audit).
 */
export function Tabs(props: TabsProps) {
  const { tabs, active, onChange, trailing, className = '' } = props;
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const focusTab = (id: string) => {
    tabRefs.current[id]?.focus();
  };

  const handleKey = (e: KeyboardEvent<HTMLButtonElement>, idx: number) => {
    const last = tabs.length - 1;
    switch (e.key) {
      case 'ArrowRight': {
        e.preventDefault();
        const next = idx === last ? 0 : idx + 1;
        onChange(tabs[next].id);
        focusTab(tabs[next].id);
        return;
      }
      case 'ArrowLeft': {
        e.preventDefault();
        const prev = idx === 0 ? last : idx - 1;
        onChange(tabs[prev].id);
        focusTab(tabs[prev].id);
        return;
      }
      case 'Home': {
        e.preventDefault();
        onChange(tabs[0].id);
        focusTab(tabs[0].id);
        return;
      }
      case 'End': {
        e.preventDefault();
        onChange(tabs[last].id);
        focusTab(tabs[last].id);
        return;
      }
      default:
        return;
    }
  };

  return (
    <div className={[
      'flex items-center justify-between gap-3 border-b border-neutral-200 bg-white/70 px-4 backdrop-blur dark:border-neutral-800 dark:bg-neutral-950/70',
      className,
    ].join(' ')}>
      <nav
        role="tablist"
        aria-label="Main sections"
        className="-mb-px flex h-14 min-w-0 flex-1 items-center gap-2 overflow-x-auto"
      >
        {tabs.map((tab, idx) => {
          const isActive = tab.id === active;
          return (
            <button
              key={tab.id}
              ref={(el) => {
                tabRefs.current[tab.id] = el;
              }}
              id={`tab-${tab.id}`}
              role="tab"
              type="button"
              tabIndex={isActive ? 0 : -1}
              aria-selected={isActive}
              aria-controls={`panel-${tab.id}`}
              onClick={() => onChange(tab.id)}
              onKeyDown={(e) => handleKey(e, idx)}
              className={[
                'inline-flex h-11 cursor-pointer items-center gap-2 rounded-md px-4 text-sm font-medium motion-safe:transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                isActive
                  ? 'bg-neutral-100 text-neutral-900 dark:bg-neutral-800 dark:text-neutral-50'
                  : 'text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100',
              ].join(' ')}
            >
              {tab.icon ? <span aria-hidden="true">{tab.icon}</span> : null}
              <span>{tab.label}</span>
            </button>
          );
        })}
      </nav>
      {trailing ? <div className="flex shrink-0 items-center gap-2">{trailing}</div> : null}
    </div>
  );
}
